import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import api, { downloadFile, getMediaBlobUrl } from '../../utils/api';
import { 
  CalendarDays, Download, Search, FileText, Eye, X, Loader2, Filter, Clock, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import BackButton from '../../components/BackButton';

// Year configuration with colors
const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020];
const YEAR_COLORS = {
  2026: { gradient: 'from-blue-500 to-indigo-600', badge: 'bg-blue-500', text: 'text-blue-400', border: 'border-blue-500/30' },
  2025: { gradient: 'from-emerald-500 to-teal-600', badge: 'bg-emerald-500', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  2024: { gradient: 'from-violet-500 to-purple-600', badge: 'bg-violet-500', text: 'text-violet-400', border: 'border-violet-500/30' },
  2023: { gradient: 'from-amber-500 to-orange-600', badge: 'bg-amber-500', text: 'text-amber-400', border: 'border-amber-500/30' },
  2022: { gradient: 'from-rose-500 to-pink-600', badge: 'bg-rose-500', text: 'text-rose-400', border: 'border-rose-500/30' },
  2021: { gradient: 'from-cyan-500 to-sky-600', badge: 'bg-cyan-500', text: 'text-cyan-400', border: 'border-cyan-500/30' },
  2020: { gradient: 'from-slate-500 to-gray-600', badge: 'bg-slate-500', text: 'text-slate-400', border: 'border-slate-500/30' },
};

const getYearColor = (date) => {
  const year = new Date(date).getFullYear();
  return YEAR_COLORS[year] || YEAR_COLORS[2020];
};

const MemberAgenda = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Filter & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      const response = await api.get('/agendas');
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load agendas');
    } finally {
      setLoading(false);
    }
  };

  // Filtered items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()));
      const itemYear = new Date(item.meeting_date).getFullYear();
      const matchesYear = selectedYear === 'all' || itemYear === parseInt(selectedYear);
      return matchesSearch && matchesYear;
    });
  }, [items, searchQuery, selectedYear]);

  // Stats
  const stats = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return {
      total: items.length,
      thisYear: items.filter(i => new Date(i.meeting_date).getFullYear() === currentYear).length,
      withDocs: items.filter(i => i.file_url).length,
      upcoming: items.filter(i => new Date(i.meeting_date) > new Date()).length,
    };
  }, [items]);

  const openPreview = async (item) => {
    if (!item.file_url) {
      toast.info('No document attached to this agenda');
      return;
    }
    setPreviewItem(item);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const url = await getMediaBlobUrl(item.file_url);
      setPreviewUrl(url);
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewItem(null);
    if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const handleDownload = async (item) => {
    if (!item.file_url) {
      toast.info('No document attached to this agenda');
      return;
    }
    try {
      await downloadFile(item.file_url, item.file_name || item.title);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="member-agenda-page">
      <BackButton to="/member" />
      
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-4 rounded-2xl shadow-lg">
          <CalendarDays className="w-8 h-8 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-serif font-bold text-slate-900">Meeting Agendas</h1>
          <p className="text-slate-400">View upcoming and past meeting agendas</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-400 text-sm font-medium">Total Agendas</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            <CalendarDays className="w-8 h-8 text-indigo-500/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-400 text-sm font-medium">Upcoming</p>
              <p className="text-2xl font-bold text-slate-900">{stats.upcoming}</p>
            </div>
            <Clock className="w-8 h-8 text-emerald-500/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-400 text-sm font-medium">This Year</p>
              <p className="text-2xl font-bold text-slate-900">{stats.thisYear}</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-500/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-400 text-sm font-medium">With Documents</p>
              <p className="text-2xl font-bold text-slate-900">{stats.withDocs}</p>
            </div>
            <FileText className="w-8 h-8 text-amber-500/50" />
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
            placeholder="Search agendas..." 
            className="pl-10 bg-slate-700 border-slate-600 text-white"
            data-testid="agenda-search-input"
          />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-slate-400" />
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-36 bg-slate-700 border-slate-600 text-white" data-testid="year-filter-select">
              <SelectValue placeholder="All Years" />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="all">All Years</SelectItem>
              {YEARS.map(year => <SelectItem key={year} value={year.toString()}>{year}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Year Filter Pills */}
      <div className="flex flex-wrap gap-2">
        <button 
          onClick={() => setSelectedYear('all')} 
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedYear === 'all' ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
        >
          All Years
        </button>
        {YEARS.map(year => {
          const colors = YEAR_COLORS[year];
          const count = items.filter(i => new Date(i.meeting_date).getFullYear() === year).length;
          return (
            <button 
              key={year} 
              onClick={() => setSelectedYear(year.toString())} 
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center space-x-2 ${selectedYear === year.toString() ? `bg-gradient-to-r ${colors.gradient} text-white shadow-lg` : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
            >
              <span>{year}</span>
              <span className={`text-xs ${selectedYear === year.toString() ? 'bg-white/20' : 'bg-slate-600'} px-2 py-0.5 rounded-full`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Preview Modal */}
      {previewOpen && previewItem && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="relative max-w-4xl w-full max-h-[90vh] bg-slate-800 rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div>
                <h3 className="font-semibold text-white">{previewItem.title}</h3>
                <p className="text-sm text-slate-400">{formatDate(previewItem.meeting_date)}</p>
              </div>
              <div className="flex items-center space-x-2">
                <Button onClick={() => handleDownload(previewItem)} className="bg-indigo-600 hover:bg-indigo-700">
                  <Download className="w-4 h-4 mr-2" />Download
                </Button>
                <button onClick={closePreview} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="h-[70vh] bg-slate-900">
              {previewLoading ? (
                <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>
              ) : previewUrl ? (
                <iframe src={previewUrl} className="w-full h-full" title="Document Preview" />
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">Preview not available. Click Download to view.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>
      ) : filteredItems.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => {
            const colors = getYearColor(item.meeting_date);
            const isUpcoming = new Date(item.meeting_date) > new Date();
            return (
              <div 
                key={item.id} 
                className={`group bg-slate-800 rounded-2xl overflow-hidden border-2 ${colors.border} hover:border-indigo-500/50 transition-all duration-300 hover:shadow-xl`}
                data-testid={`agenda-card-${item.id}`}
              >
                <div className={`h-2 bg-gradient-to-r ${colors.gradient}`} />
                <div className="p-5">
                  <div className="flex items-center space-x-2 mb-4">
                    <span className={`${colors.badge} text-white text-xs font-bold px-2 py-1 rounded-full`}>
                      {new Date(item.meeting_date).getFullYear()}
                    </span>
                    {isUpcoming && (
                      <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center">
                        <Clock className="w-3 h-3 mr-1" />Upcoming
                      </span>
                    )}
                  </div>
                  
                  <h3 className="font-semibold text-lg text-white mb-2 line-clamp-2 group-hover:text-indigo-400 transition-colors">{item.title}</h3>
                  
                  <div className="flex items-center text-sm text-slate-400 mb-3">
                    <Calendar className="w-4 h-4 mr-2" />
                    {formatDate(item.meeting_date)}
                  </div>
                  
                  {item.description && (
                    <p className="text-sm text-slate-400 mb-4 line-clamp-3">{item.description}</p>
                  )}
                  
                  {item.file_url && (
                    <div className="flex items-center text-sm text-indigo-400 mb-4">
                      <FileText className="w-4 h-4 mr-2" />
                      {item.file_name || 'Document attached'}
                    </div>
                  )}
                  
                  {item.file_url && (
                    <div className="flex space-x-2">
                      <Button onClick={() => openPreview(item)} variant="outline" size="sm" data-testid="preview-btn" className="flex-1 bg-slate-700 text-white border-slate-500 hover:bg-slate-600 hover:text-white hover:border-indigo-400">
                        <Eye className="w-4 h-4 mr-1" />Preview
                      </Button>
                      <Button onClick={() => handleDownload(item)} size="sm" className={`flex-1 bg-gradient-to-r ${colors.gradient}`}>
                        <Download className="w-4 h-4 mr-1" />Download
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-2xl text-center py-16">
          <CalendarDays className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 text-lg">{selectedYear === 'all' ? 'No agendas available yet' : `No agendas for ${selectedYear}`}</p>
          <p className="text-sm text-slate-500 mt-2">Check back later for upcoming meeting agendas</p>
        </div>
      )}
    </div>
  );
};

export default MemberAgenda;
